import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantDataBirthday } from '../../util/helpers/applicantDataHelper';
import { calculateAge } from '../../util/helpers/datesHelpers';
import { APPLICANT_TYPES } from '../../util/constants';
import useStepPost from '../../hooks/useStepPost';
import usePersonDateOfBirthDispatch from '../../hooks/usePersonDateOfBirthDispatch';
import { FormWrap } from '../FormContainer/FormContainer';
import InputText, { ErrorMessage } from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import useApplicationOwnerName from '../../hooks/useApplicationOwnerName';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import { useGetIsImpersonating } from '../../util/hooks/useGetIsImpersonating';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';

const DateOfBirthFormFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 100px 100px 100px;
  grid-gap: 32px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 100%;
`;

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    fields: ['Month', 'Year', 'Day'],
  }),
});

const formValidationSchemaImpersonate = Yup.object().shape(
  {
    ...getValidationSchemaImpersonate({
      fields: ['Month', 'Year', 'Day'],
    }),
  },
  [
    ['Month', 'Month'],
    ['Day', 'Day'],
    ['Year', 'Year'],
  ],
); // cyclic dependency);

const AccountDobForm = ({
  nextPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const dayRef = useRef();
  const yearRef = useRef();
  const hiddenRef = useRef();
  const monthRef = useRef();
  const { t } = useTranslation();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { personDateOfBirthDispatch } = usePersonDateOfBirthDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const ApplicantData = useApplicantData(applicantType);
  const applicantName = useApplicationOwnerName(applicantType);
  const { isSalesAssisted } = useGetIsImpersonating();
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const defaultValues = {
    Month: getApplicantDataBirthday(ApplicantData, 'PersonBirthday').Month,
    Year: getApplicantDataBirthday(ApplicantData, 'PersonBirthday').Year,
    Day: getApplicantDataBirthday(ApplicantData, 'PersonBirthday').Day,
  };
  const { handleSubmit, setError, control, watch, clearErrors } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues,
  });

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName?.FirstName
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Account Dob',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
      });
      return;
    }
    if (ApplicantData?.PersonName?.FirstName) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Account Dob Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantData?.PersonName?.FirstName]);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Account Dob',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Account Dob Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
    });
  };

  const onSubmit = ({ Month, Day, Year }) => {
    const errorSet = (name, message) => {
      setError(name, {
        type: 'string',
        message,
      });
    };
    if (isSalesAssisted && (!Month || !Day || !Year)) {
      personDateOfBirthDispatch({
        personUid: ApplicantData?.PersonUid,
        defaultValues,
        data: {
          Month,
          Year,
          Day,
        },
        onSuccess:
          eventType === 'Form'
            ? onSuccess
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError: () => {
          setHasError(true);
          setSaveAndCloseModalOpen(true);
        },
      });

      return;
    }

    if (!ApplicantData?.PersonUid) {
      return;
    }

    const age = calculateAge(Year, Month, Day);

    const dateOfBirthMinor = new Date(Year, Month - 1, Day);
    const currentDate = new Date();
    if (applicantType === APPLICANT_TYPES.MINOR && age >= 21) {
      errorSet('Year', '');
      errorSet('Month', '');
      errorSet('Day', '');
      setErrorMessage(t('fields.errors.validation.dateOfBirthUtma'));
      return;
    }

    if (
      applicantType === APPLICANT_TYPES.MINOR &&
      currentDate.getTime() <= dateOfBirthMinor.getTime()
    ) {
      errorSet('Year', '');
      errorSet('Month', '');
      errorSet('Day', '');
      setErrorMessage(t('fields.errors.validation.dateOfBirthInvalid'));
      return;
    }

    if (applicantType === APPLICANT_TYPES.SECONDARY && age < 21) {
      errorSet('Year', '');
      errorSet('Month', '');
      errorSet('Day', '');
      setErrorMessage(
        t('fields.errors.validation.dateOfBirthSecondary', { applicantName }),
      );
      return;
    }

    if (applicantType !== APPLICANT_TYPES.MINOR && age < 21) {
      errorSet('Year', '');
      errorSet('Month', '');
      errorSet('Day', '');
      setErrorMessage(t('fields.errors.validation.dateOfBirth'));

      return;
    }

    personDateOfBirthDispatch({
      personUid: ApplicantData?.PersonUid,
      defaultValues,
      data: {
        Month,
        Year,
        Day,
      },
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError: () => {
        setHasError(true);
        setSaveAndCloseModalOpen(true);
      },
    });
    handleGtmEvent();
  };

  const Month = watch('Month');
  const Year = watch('Year');
  const Day = watch('Day');

  useEffect(() => {
    const age = calculateAge(Year, Month, Day);
    if (applicantType === APPLICANT_TYPES.MAIN && age >= 21) {
      clearErrors();
    }
    if (applicantType !== APPLICANT_TYPES.MINOR && age <= 21) {
      clearErrors();
    }
    if (applicantType === APPLICANT_TYPES.MINOR && age >= 21) {
      clearErrors();
    }
    if (applicantType === APPLICANT_TYPES.SECONDARY && age < 21) {
      clearErrors();
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Month, Year, Day]);

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <DateOfBirthFormFieldsContainer>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.month')}
              {...field}
              {...fieldState}
              type="number"
              autocomplete="off"
              autoFocus
              placeholder="MM"
              onInput={(e) => {
                e.target.value = e.target.value.toString().slice(0, 2);
                if (e.target.value.length === 2) {
                  return dayRef.current?.focus();
                }
              }}
              ref={monthRef}
            />
          )}
          name="Month"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.day')}
              {...field}
              {...fieldState}
              type="number"
              autocomplete="off"
              placeholder="DD"
              onInput={(e) => {
                e.target.value = e.target.value.toString().slice(0, 2);
                if (e.target.value.length === 2) {
                  return yearRef.current?.focus();
                }
              }}
              ref={dayRef}
            />
          )}
          name="Day"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.year')}
              {...field}
              {...fieldState}
              type="number"
              autocomplete="off"
              placeholder="YYYY"
              ref={yearRef}
              onInput={(e) => {
                e.target.value = e.target.value.toString().slice(0, 4);
                if (e.target.value.length === 4) {
                  return hiddenRef.current?.focus();
                }
              }}
            />
          )}
          name="Year"
        />
        <input
          type="text"
          style={{
            opacity: 0,
            cursor: 'context-menu',
            width: '0px',
            height: '0px',
          }}
          ref={hiddenRef}
        />
      </DateOfBirthFormFieldsContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={nextPath ? t('continue') : t('update')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

AccountDobForm.propTypes = {
  nextPath: PropTypes.string,
  path: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default AccountDobForm;
